import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { Plus, Minus, ArrowUpRight } from "lucide-react";
import { faqItems } from "@/data/faq";

export default function Faq() {
  useRevealOnScroll();
  const [openIdx, setOpenIdx] = useState(0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title="FAQ | Wagenhofer Capital – Häufige Fragen"
        description="Antworten auf die häufigsten Fragen zu Wagenhofer Capital: Beteiligungsfokus, Region, Prozess, Diskretion, Unternehmensnachfolge und mehr."
        path="/faq"
        jsonLd={jsonLd}
      />
      <Navigation />
      <Breadcrumbs items={[{ label: "FAQ", to: "/faq" }]} />

      <PageHeader
        testId="page-faq"
        overline="Häufige Fragen"
        title={
          <>
            Klarheit
            <br />
            <span className="text-zinc-400">vor dem ersten Gespräch.</span>
          </>
        }
        subtitle="Die wichtigsten Fragen zu unserem Selbstverständnis, unserem Prozess und unserer Arbeitsweise – kompakt beantwortet."
      />

      <section
        data-testid="section-faq"
        className="section-y border-b border-white/5"
      >
        <div className="container-x max-w-4xl mx-auto">
          <ul className="border-t border-white/10">
            {faqItems.map((item, idx) => {
              const open = openIdx === idx;
              return (
                <li
                  key={item.q}
                  data-testid={`faq-item-${idx + 1}`}
                  className="border-b border-white/10"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : idx)}
                    aria-expanded={open}
                    className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
                  >
                    <span className="flex items-baseline gap-5 flex-1">
                      <span className="text-[#D4AF37] text-xs tracking-[0.2em] pt-1">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display text-lg md:text-xl tracking-tight font-medium leading-snug">
                        {item.q}
                      </span>
                    </span>
                    <span className="shrink-0 mt-1.5 text-zinc-500 group-hover:text-[#D4AF37] transition-colors">
                      {open ? (
                        <Minus className="w-4 h-4" strokeWidth={1.5} />
                      ) : (
                        <Plus className="w-4 h-4" strokeWidth={1.5} />
                      )}
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-500 ease-out ${
                      open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="pb-7 pl-12 pr-2 text-zinc-300 leading-relaxed font-light text-[15px] md:text-base max-w-3xl">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="reveal">
            <div className="overline mb-3">Ihre Frage nicht dabei?</div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
              Schreiben Sie uns – wir antworten zeitnah.
            </h2>
          </div>
          <Link
            to="/kontakt"
            data-testid="faq-cta-contact"
            className="reveal delay-1 group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold"
          >
            Kontakt aufnehmen
            <ArrowUpRight
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={1.5}
            />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
